import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { galleryImages } from '../mockData';
import { X, ZoomIn } from 'lucide-react';
import { FadeIn, StaggerContainer, StaggerItem } from '../components/PageTransition';

export const GalleryPage = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [filter, setFilter] = useState('all');

  const categories = [
    { id: 'all', label: 'All Photos' },
    { id: 'dishes', label: 'Dishes' },
    { id: 'sweets', label: 'Sweets' },
    { id: 'ambience', label: 'Ambience' }
  ];

  const categorizedImages = galleryImages.map((img, idx) => ({
    src: img,
    category: idx < 4 ? 'sweets' : idx < 8 ? 'dishes' : 'ambience'
  }));

  const filteredImages = filter === 'all'
    ? categorizedImages
    : categorizedImages.filter(img => img.category === filter);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-96 flex items-center justify-center overflow-hidden bg-gradient-to-r from-amber-900 to-amber-800 mt-[150px]">
        <motion.div
          className="absolute inset-0 opacity-20"
          animate={{
            backgroundPosition: ['0% 0%', '100% 100%'],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            repeatType: 'reverse',
          }}
          style={{
            backgroundImage: 'url("https://images.unsplash.com/photo-1695568181440-aca4dac18650")',
            backgroundSize: 'cover',
          }}
        />
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 z-10 text-center">
          <motion.h1
            className="text-5xl sm:text-6xl font-bold text-white mb-6"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            Our Gallery
          </motion.h1>
          <motion.p
            className="text-xl text-amber-100 max-w-2xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            A visual journey through our authentic Bengali dishes and cozy ambience
          </motion.p>
        </div>
      </section>

      {/* Filter Section */}
      <section className="py-12 bg-white border-b border-gray-200">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <div className="flex flex-wrap justify-center gap-3">
              {categories.map((category) => (
                <motion.button
                  key={category.id}
                  onClick={() => setFilter(category.id)}
                  className={`px-6 py-3 rounded-lg font-medium transition-all duration-300 ${
                    filter === category.id
                      ? 'bg-amber-700 text-white shadow-lg'
                      : 'bg-amber-100 text-amber-900 hover:bg-amber-200'
                  }`}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {category.label}
                </motion.button>
              ))}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="py-20 bg-gradient-to-b from-white to-amber-50/30">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <StaggerContainer staggerDelay={0.1}>
            <motion.div
              layout
              className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4"
            >
              <AnimatePresence>
                {filteredImages.map((image, index) => (
                  <StaggerItem key={index}>
                    <motion.div
                      layout
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.8 }}
                      transition={{ duration: 0.5 }}
                      className="relative group cursor-pointer overflow-hidden rounded-xl aspect-square"
                      onClick={() => setSelectedImage(image.src)}
                      whileHover={{ scale: 1.05 }}
                    >
                      <img
                        src={image.src}
                        alt={`Gallery ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                      <motion.div
                        className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                      >
                        <ZoomIn className="w-12 h-12 text-white" />
                      </motion.div>
                    </motion.div>
                  </StaggerItem>
                ))}
              </AnimatePresence>
            </motion.div>
          </StaggerContainer>
        </div>
      </section>

      {/* Lightbox Modal */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedImage(null)}
          >
            <motion.button
              className="absolute top-4 right-4 text-white hover:text-amber-500 transition-colors duration-300 z-50"
              onClick={() => setSelectedImage(null)}
              whileHover={{ scale: 1.1, rotate: 90 }}
              whileTap={{ scale: 0.9 }}
            >
              <X className="w-10 h-10" />
            </motion.button>
            <motion.img
              src={selectedImage}
              alt="Gallery"
              className="max-w-full max-h-full object-contain rounded-lg"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ duration: 0.3 }}
              onClick={(e) => e.stopPropagation()}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* CTA Section */}
      <FadeIn>
        <section className="py-20 bg-amber-900">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-4xl font-bold text-white mb-6">
              Experience It Yourself
            </h2>
            <p className="text-xl text-amber-100 mb-8 max-w-2xl mx-auto">
              Visit us and taste the authentic flavors of Bengal
            </p>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => window.location.href = '/reservations'}
              className="bg-white text-amber-900 hover:bg-amber-50 px-8 py-4 text-lg rounded-lg font-medium transition-all duration-300"
            >
              Book a Table
            </motion.button>
          </div>
        </section>
      </FadeIn>
    </div>
  );
};

export default GalleryPage;
